import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  focusToNextInput,
  focusToPrevInput,
  isDigit,
} from "./OtpField.constants";

type UseOtpInputProps = {
  value: string;
  valueLength: number;
  onChange?: (value: string) => void;
};

type UseOtpInputReturn = {
  valueItems: {
    digit: string;
    position: number;
  }[];
  firstNumberInput: RefObject<HTMLInputElement | null>;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>, idx: number) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  handleFocus: (e: FocusEvent<HTMLInputElement>) => void;
};

export const useOtpInput = ({
  value,
  valueLength,
  onChange,
}: UseOtpInputProps): UseOtpInputReturn => {
  const firstNumberInput = useRef<HTMLInputElement | null>(null);

  // Transform the input value into an array of digits
  const valueItems = useMemo(
    () =>
      Array(valueLength)
        .fill("")
        .map((_, position) => {
          const digit = value[position];

          return isDigit(digit) ? { digit, position } : { digit: "", position };
        }),
    [value, valueLength],
  );

  // Reset focus to first input when value is cleared
  useEffect(() => {
    if (value === "") {
      firstNumberInput.current?.focus();
    }
  }, [value]);

  // Input handlers
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, idx: number) => {
    const target = e.target;
    const targetValue = target.value.trim();
    const isTargetValueDigit = isDigit(targetValue);

    // Ignore non-digit input unless it's empty
    if (!isTargetValueDigit && targetValue !== "") {
      return;
    }

    const nextInputEl = target.nextElementSibling as HTMLInputElement | null;

    // Only delete digit if next input element has no value
    if (!isTargetValueDigit && nextInputEl && nextInputEl.value !== "") {
      return;
    }

    const finalValue = isTargetValueDigit ? targetValue : " ";

    // Handle single digit input
    if (targetValue.length === 1) {
      const newValue =
        value.substring(0, idx) + finalValue + value.substring(idx + 1);
      onChange?.(newValue.trim());

      if (isTargetValueDigit) {
        focusToNextInput(target);
      }
    }
    // Handle paste of entire code
    else if (targetValue.length === valueLength) {
      onChange?.(targetValue);
      target.blur();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const target = e.target as HTMLInputElement;

    // Handle arrow key navigation
    if (key === "ArrowRight" || key === "ArrowDown") {
      e.preventDefault();

      return focusToNextInput(target);
    }

    if (key === "ArrowLeft" || key === "ArrowUp") {
      e.preventDefault();

      return focusToPrevInput(target);
    }

    // Keep the selection range position if the same digit was typed
    target.setSelectionRange(0, target.value.length);

    // Handle backspace on empty field
    if (key === "Backspace" && target.value === "") {
      focusToPrevInput(target);
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    const { target } = e;

    // Focus previous input if it's empty
    const prevInputEl =
      target.previousElementSibling as HTMLInputElement | null;

    if (prevInputEl && prevInputEl.value === "") {
      return prevInputEl.focus();
    }

    target.setSelectionRange(0, target.value.length);
  };

  return {
    valueItems,
    firstNumberInput,
    handleInputChange,
    handleKeyDown,
    handleFocus,
  };
};
